import { useHingeHealthSecurityContext } from '@hinge-health/react-component-library';
import { SecurityContext } from '@hinge-health/react-component-library/dist/components/auth/hinge-health-security-context';
import { useCallback, useState } from 'react';

type AuthClient = SecurityContext['hingeHealthAuthClient'];

// ---------------------------------------------------------------------------
// BFF wire types.
//
// Both endpoints are REST, not GraphQL, so there is no codegen and drift is not
// a compile error. Keep this block in sync by hand with operations-hub-bff:
//   src/modules/eligibility/eligibility/types.ts   (legacy)
//   src/modules/hinge-select/types.ts              (internal)
// ---------------------------------------------------------------------------

export interface LegacyPatientInfo {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  sex?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  relationship?: string;
  subscriberId?: string;
}

export interface LegacyCoverageDetails {
  efileRecord?: Record<string, unknown>;
  primarySubscriberEfileRecord?: Record<string, unknown>;
  // The provider's raw payload, forwarded verbatim. For ESI this carries the
  // `enrollment` and `membership` blocks the ESI invoice writer reads.
  upstreamApiContent?: Record<string, unknown>;
  apiSource?: string;
}

export interface LegacyCoverageInfo {
  coverageId?: number;
  patientInfo?: LegacyPatientInfo;
  primarySubscriberInfo?: LegacyPatientInfo;
  coverageDetails?: LegacyCoverageDetails;
}

export interface LegacyEligibilityStatus {
  isEligible: boolean;
  dateChecked?: string;
  coverageId?: number;
  clientId?: number;
  insurerId?: number;
  contractId?: number;
  accountName?: string;
  coverageInfo?: LegacyCoverageInfo;
  reason?: string;
  errors?: unknown[];
  eligibilitySource?: string;
}

export interface InternalEligibilityAddress {
  address1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  countryCode?: string;
}

export interface InternalEligibilityPerson {
  personId?: string;
  firstNameLegal?: string;
  firstNamePreferred?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  sex?: string;
  address?: InternalEligibilityAddress;
  subscriberId?: string;
  groupNumber?: string;
}

export interface InternalVerificationState {
  // RESPONSE_STATUS_COMPLETE means the call to the provider completed. It is
  // NOT an eligibility indicator — isBillingEligible is.
  state?: string;
  // google.protobuf.Struct. The BFF deep-camelcases hinge-select responses, so
  // keys inside this blob are camelCased too — see utils/eligibility-compare/esi.
  rawResponse?: Record<string, unknown>;
  provider?: string;
  externalEligibilityResponseId?: string;
}

export interface InternalEligibilityResponse {
  memberAccountId: string;
  hhId?: string;
  states?: {
    accountState?: Record<string, unknown>;
    planState?: Record<string, unknown>;
    verificationState?: InternalVerificationState;
  };
  insurerId?: number;
  inquiryDate?: string;
  patient?: InternalEligibilityPerson;
  subscriber?: InternalEligibilityPerson;
  relationship?: string;
  clientId?: string;
  evaluationId?: string;
  isBillingEligible: boolean;
  fromCache?: boolean;
}

export enum EligibilityCheckUseCase {
  COVERAGE = 'ELIGIBILITY_CHECK_USE_CASE_COVERAGE',
  BENEFIT = 'ELIGIBILITY_CHECK_USE_CASE_BENEFIT',
}

// Response of GET /hinge-select/member-accounts/get-by-hh-id — used to resolve
// the member account id before the internal eligibility call can be made.
export interface GetMemberAccountResponse {
  memberAccount: {
    memberAccountId: string;
  };
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export class BffHttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'BffHttpError';
  }
}

const bffRoot = (): string =>
  (process.env.REACT_APP_BFF_ROOT ?? '').replace(/\/+$/, '');

const get = async <T>(
  client: AuthClient,
  path: string,
  params: Record<string, string>,
): Promise<T> => {
  const token = await client.getOrRenewAccessToken();
  if (!token) {
    throw new Error('Token expired and could not be renewed — sign in again');
  }
  const res = await fetch(
    `${bffRoot()}${path}?${new URLSearchParams(params).toString()}`,
    {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      // A retried GET here is a duplicate — and possibly billable — eligibility
      // check. The BFF also sends Cache-Control: no-store.
      cache: 'no-store',
    },
  );
  if (!res.ok) {
    throw new BffHttpError(
      res.status,
      res.status === 403
        ? 'Forbidden — your token lacks the phiaccess claim'
        : `Request failed with HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
};

const asError = (reason: unknown): Error =>
  reason instanceof Error ? reason : new Error(String(reason));

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface VerifierParams {
  hhUserUuid: string;
  inquiryDate: string;
  useCase: EligibilityCheckUseCase;
  fromCache: boolean;
}

export interface VerifierResult {
  legacy?: LegacyEligibilityStatus;
  legacyError?: Error;
  internal?: InternalEligibilityResponse;
  internalError?: Error;
  // Resolved from hhUserUuid via GET .../member-accounts/get-by-hh-id, ahead of
  // (and required for) the internal eligibility call.
  memberAccountId?: string;
  memberAccountError?: Error;
}

interface UseEligibilityVerifierReturn {
  run: (params: VerifierParams) => Promise<void>;
  result?: VerifierResult;
  loading: boolean;
}

export const useEligibilityVerifier = (): UseEligibilityVerifierReturn => {
  const { hingeHealthAuthClient } = useHingeHealthSecurityContext();
  const [result, setResult] = useState<VerifierResult | undefined>();
  const [loading, setLoading] = useState(false);

  const run = useCallback(
    async (params: VerifierParams): Promise<void> => {
      setLoading(true);
      setResult(undefined);
      // Created once, awaited twice below (chained into the internal call, and
      // observed directly for its own success/failure) — allSettled, not all,
      // since one path failing is itself a finding and the other side's
      // payload is still worth showing.
      const memberAccountLookup = get<GetMemberAccountResponse>(
        hingeHealthAuthClient,
        '/hinge-select/member-accounts/get-by-hh-id',
        { hhId: params.hhUserUuid },
      );

      const [legacy, internal, memberAccount] = await Promise.allSettled([
        get<LegacyEligibilityStatus>(
          hingeHealthAuthClient,
          '/eligibility/user',
          {
            userId: params.hhUserUuid,
            inquiryDate: params.inquiryDate,
          },
        ),
        memberAccountLookup.then(({ memberAccount: acct }) =>
          get<InternalEligibilityResponse>(
            hingeHealthAuthClient,
            '/hinge-select/eligibility/internal',
            {
              memberAccountId: acct.memberAccountId,
              useCase: params.useCase,
              inquiryDate: params.inquiryDate,
              fromCache: String(params.fromCache),
              // benefitType deliberately omitted — downstream defaults to
              // BENEFIT_TYPE_DIGITAL_PHYSICAL_THERAPY.
            },
          ),
        ),
        memberAccountLookup,
      ]);
      setResult({
        legacy: legacy.status === 'fulfilled' ? legacy.value : undefined,
        legacyError:
          legacy.status === 'rejected' ? asError(legacy.reason) : undefined,
        internal: internal.status === 'fulfilled' ? internal.value : undefined,
        internalError:
          internal.status === 'rejected' ? asError(internal.reason) : undefined,
        memberAccountId:
          memberAccount.status === 'fulfilled'
            ? memberAccount.value.memberAccount.memberAccountId
            : undefined,
        memberAccountError:
          memberAccount.status === 'rejected'
            ? asError(memberAccount.reason)
            : undefined,
      });
      setLoading(false);
    },
    [hingeHealthAuthClient],
  );

  return { run, result, loading };
};
